<template>
  <div class="steps-container">
    <div class="steps-header flex items-center justify-between mb-6 px-[4px]">
      <div class="steps-indicators flex">
        <div
          v-for="(_, index) in stepsCount"
          :key="index"
          class="step-indicator flex items-center"
          @click="canGoToStep(index) && goToStep(index)"
          :class="{ 'cursor-not-allowed': !canGoToStep(index) }"
        >
          <div
            class="step-circle w-8 h-8 z-[2] rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200"
            :class="{
              'bg-primary text-white': activeStep > index,
              'bg-primary text-white ring-2 ring-offset-2 ring-primary': activeStep === index,
              'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400': activeStep < index,
              'cursor-pointer': canGoToStep(index),
              'cursor-not-allowed': !canGoToStep(index)
            }"
          >
            <span v-if="activeStep > index" class="step-completed fa-light fa-check"></span>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div
            v-if="index < stepsCount - 1"
            class="step-line w-10 h-1"
            :class="{
              'bg-primary': activeStep > index,
              'bg-gray-200 dark:bg-gray-700': activeStep <= index
            }"
          ></div>
        </div>
      </div>
      <div v-if="showStepTitles" class="hidden md:block text-sm font-medium">
        {{ currentStepTitle }}
      </div>
    </div>

    <div class="steps-content relative">
      <slot></slot>
    </div>

    <div class="steps-navigation flex justify-between mt-10">
      <button
        v-if="activeStep > 0"
        @click="prevStep"
        class="jmiBtn grey prev-step-btn"
      >
        Back
      </button>
      <div v-else class="w-20"></div>
      
      <button
        v-if="activeStep < stepsCount - 1"
        @click="nextStep"
        class="jmiBtn green next-step-btn"
        :disabled="isStepDisabled"
      >
        Next
      </button>
      <button
        v-else
        @click="complete"
        class="jmiBtn green complete-btn"
        :disabled="isStepDisabled"
      >
        Finish
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, provide, computed, onMounted, watch, nextTick } from 'vue'
import { buttonVariants } from '../button'

const props = defineProps({
  initialStep: {
    type: Number,
    default: 0
  },
  showStepTitles: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['complete', 'step-change'])

const stepsData = ref([])
const activeStep = ref(props.initialStep)
const stepsCount = ref(0)

watch(() => props.initialStep, (newValue) => {
  activeStep.value = newValue
})

const countSteps = () => {
  stepsCount.value = Math.max(1, document.querySelectorAll('.step').length)
}

const updateStepsVisibility = async () => {
  await nextTick()
  const steps = document.querySelectorAll('.step')
  steps.forEach((step, index) => {
    if (index === activeStep.value) {
      step.classList.add('active')
    } else {
      step.classList.remove('active')
    }
  })
}

watch(() => activeStep.value, () => {
  updateStepsVisibility()
})

const currentStepTitle = computed(() => {
  const step = stepsData.value.find(step => step.index === activeStep.value)
  return step?.title || `Step ${activeStep.value + 1}`
})

const currentStep = computed(() => 
  stepsData.value.find(step => step.index === activeStep.value)
)

const isCurrentStepValid = computed(() => {
  const step = currentStep.value
  if (step && step.isValid === false) return false
  return true
})

const isCurrentStepRequired = computed(() => {
  const step = currentStep.value
  return step?.isRequired === true
})

const isStepDisabled = computed(() => {
  return isCurrentStepRequired.value && !isCurrentStepValid.value
})

const canGoToStep = (index) => {
  if (index < activeStep.value) return true
  if (index === activeStep.value) return true
  
  for (let i = activeStep.value; i < index; i++) {
    const step = stepsData.value.find(s => s.index === i)
    if (step?.isRequired && step?.isValid === false) {
      return false
    }
  }
  
  return true
}

const nextStep = () => {
  if (activeStep.value < stepsCount.value - 1 && !isStepDisabled.value) {
    activeStep.value++
    emit('step-change', activeStep.value)
  }
}

const prevStep = () => {
  if (activeStep.value > 0) {
    activeStep.value--
    emit('step-change', activeStep.value)
  }
}

const goToStep = (index) => {
  if (index >= 0 && index < stepsCount.value) {
    activeStep.value = index
    emit('step-change', activeStep.value)
  }
}

const complete = () => {
  if (!isStepDisabled.value) {
    emit('complete')
  }
}

const registerStep = (step) => {
  const existingStepIndex = stepsData.value.findIndex(s => s.id === step.id)
  
  if (existingStepIndex >= 0) {
    stepsData.value[existingStepIndex] = {
      ...stepsData.value[existingStepIndex],
      ...step
    }
  } else {
    stepsData.value.push(step)
    
    stepsData.value.sort((a, b) => a.index - b.index)
  }
  
  countSteps()
  updateStepsVisibility()
}

const unregisterStep = (id) => {
  stepsData.value = stepsData.value.filter(step => step.id !== id)
  countSteps()
}

provide('activeStep', computed(() => activeStep.value))
provide('registerStep', registerStep)
provide('unregisterStep', unregisterStep)

onMounted(async () => {
  countSteps()
  await nextTick()
  updateStepsVisibility()
  emit('step-change', activeStep.value)
})
</script>

<style>
button.prev-step-btn {
  @apply border-gray-300 bg-transparent text-gray-700;
}

button.next-step-btn,
button.complete-btn {
  @apply bg-primary text-white;
}

button:disabled {
  @apply opacity-50 cursor-not-allowed;
}

input,
select,
textarea {
  @apply w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary;
}

.dark button.prev-step-btn {
  @apply border-gray-600 bg-transparent text-gray-200;
}

.dark button.next-step-btn,
.dark button.complete-btn {
  @apply bg-primary text-white;
}

.dark button:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.dark input,
.dark select,
.dark textarea {
  @apply bg-gray-800 text-white border-gray-700;
}

.dark input::placeholder,
.dark select::placeholder,
.dark textarea::placeholder {
  @apply text-gray-400;
}

input,
select,
textarea {
  @apply w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary;
}

.cursorPointer {
  @apply cursor-pointer;
}

.steps-content {
  position: relative;
  transition: height 0.3s ease-in-out;
  overflow: hidden;
}

.steps-content > .step {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease;
}

.steps-content > .step.active {
  opacity: 1;
  visibility: visible;
  position: relative;
  z-index: 1;
}
</style> 